import {
  createContext,
  FormEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FilterField } from "../../../components/FilterModal/types";
import {
  bordaService,
  Borda,
  BordaFormData,
} from "../../../services/bordaService";
import { COLORS } from "../../../constants";
import { bordaFilterFields } from "../constants/filterFields";

type BordaFormState = {
  id: number | undefined;
  nome: string;
  lojaId: number;
};

type BordaFilters = {
  nome: string;
};

interface BordasContextType {
  bordas: Borda[];
  isLoading: boolean;
  isModalOpen: boolean;
  isFilterModalOpen: boolean;
  formState: BordaFormState;
  filterFields: FilterField[];
  tableData: Array<[string]>;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  activeFiltersCount: number;
  isSaving: boolean;
  openFilterModal: () => void;
  closeFilterModal: () => void;
  openCreateModal: () => void;
  openEditModal: (item: Borda) => void;
  closeModal: () => void;
  handleDelete: (item: Borda) => Promise<void>;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  handleFilterFieldChange: (name: string, value: string) => void;
  handleSearch: () => void;
  handleClear: () => void;
  setFormNome: (nome: string) => void;
  setCurrentPage: (page: number) => void;
}

const BordasContext = createContext<BordasContextType | undefined>(undefined);

const getDefaultFormState = (): BordaFormState => ({
  id: undefined,
  nome: "",
  lojaId: 1,
});

const getDefaultFilters = (): BordaFilters => ({
  nome: "",
});

export const BordasProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formState, setFormState] = useState<BordaFormState>(
    getDefaultFormState()
  );
  const [filters, setFilters] = useState<BordaFilters>(getDefaultFilters());
  const [appliedFilters, setAppliedFilters] =
    useState<BordaFilters>(getDefaultFilters());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: bordas = [], isLoading } = useQuery({
    queryKey: ["bordas", appliedFilters],
    queryFn: () =>
      bordaService.getBordas({
        nome: appliedFilters.nome || undefined,
      }),
  });

  const activeFiltersCount = Object.values(appliedFilters).filter(
    (value) => value !== null && value !== undefined && value !== ""
  ).length;

  const filterFields: FilterField[] = bordaFilterFields.map((field) => ({
    ...field,
    value: filters[field.name as keyof BordaFilters] || "",
  }));

  const totalItems = bordas.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBordas = bordas.slice(startIndex, endIndex);

  const tableData: Array<[string]> = paginatedBordas.map((item) => [item.nome]);

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const invalidateBordas = () => {
    queryClient.invalidateQueries({ queryKey: ["bordas"] });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormState(getDefaultFormState());
  };

  const createMutation = useMutation({
    mutationFn: (payload: BordaFormData) => bordaService.createBorda(payload),
    onSuccess: () => {
      toast.success("Borda cadastrada com sucesso.");
      invalidateBordas();
      closeModal();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: BordaFormData }) =>
      bordaService.updateBorda(payload.id, payload.data),
    onSuccess: () => {
      toast.success("Borda atualizada com sucesso.");
      invalidateBordas();
      closeModal();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => bordaService.deleteBorda(id),
    onSuccess: () => {
      toast.success("Borda removida com sucesso.");
      invalidateBordas();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const openFilterModal = () => setIsFilterModalOpen(true);
  const closeFilterModal = () => setIsFilterModalOpen(false);

  const openCreateModal = () => {
    setFormState(getDefaultFormState());
    setIsModalOpen(true);
  };

  const openEditModal = (item: Borda) => {
    setFormState({
      id: item.id,
      nome: item.nome,
      lojaId: item.lojaId,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (item: Borda) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: `Deseja remover a borda "${item.nome}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.PRIMARY,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;
    await deleteMutation.mutateAsync(item.id);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nome = formState.nome.trim();
    if (!nome) {
      toast.error("Informe o nome da borda.");
      return;
    }

    const payload: BordaFormData = {
      nome,
      lojaId: formState.id ? formState.lojaId : 1,
    };

    if (formState.id) {
      updateMutation.mutate({ id: formState.id, data: payload });
      return;
    }

    createMutation.mutate(payload);
  };

  const handleFilterFieldChange = (name: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setIsFilterModalOpen(false);
  };

  const handleClear = () => {
    const empty = getDefaultFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
  };

  const setFormNome = (nome: string) => {
    setFormState((prev) => ({ ...prev, nome }));
  };

  return (
    <BordasContext.Provider
      value={{
        bordas: paginatedBordas,
        isLoading,
        isModalOpen,
        isFilterModalOpen,
        formState,
        filterFields,
        tableData,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        activeFiltersCount,
        isSaving: createMutation.isPending || updateMutation.isPending,
        openFilterModal,
        closeFilterModal,
        openCreateModal,
        openEditModal,
        closeModal,
        handleDelete,
        handleSubmit,
        handleFilterFieldChange,
        handleSearch,
        handleClear,
        setFormNome,
        setCurrentPage,
      }}
    >
      {children}
    </BordasContext.Provider>
  );
};

export const useBordas = () => {
  const context = useContext(BordasContext);
  if (!context) {
    throw new Error("useBordas must be used within BordasProvider");
  }
  return context;
};
